<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { normalizeEventFieldKey } from '../helpers/eventFieldKey';
import { eventAutoCloseMinutesError } from '../helpers/eventAutoClosePolicy';

const EVENTS_KEY = 'lumerai_events';
const MAX_EVENTS = 20;
const MAX_QUESTIONS = 20;
const store = useStore();
const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const events = ref([]);
const collapsedEventIds = ref(new Set());
const isSaving = ref(false);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const contactFieldOptions = computed(() => [
  { value: 'name', label: t('CONTACT_SYNC.FIELD_FULL_NAME') },
  { value: 'email', label: t('CONTACT_SYNC.FIELD_EMAIL') },
  { value: 'phone_number', label: t('CONTACT_SYNC.FIELD_PHONE') },
  { value: 'identifier', label: t('CONTACT_SYNC.FIELD_IDENTIFIER') },
  {
    value: 'additional_attributes.city',
    label: t('CONTACT_SYNC.FIELD_CITY'),
  },
  {
    value: 'additional_attributes.country_code',
    label: t('CONTACT_SYNC.FIELD_COUNTRY'),
  },
  {
    value: 'additional_attributes.description',
    label: t('CONTACT_SYNC.FIELD_BIOGRAPHY'),
  },
  {
    value: 'additional_attributes.company_name',
    label: t('CONTACT_SYNC.FIELD_COMPANY'),
  },
  ...(store.getters['attributes/getContactAttributes'] || [])
    .filter(
      attribute =>
        ![
          'name',
          'email',
          'phone_number',
          'identifier',
          'city',
          'country',
          'country_code',
          'description',
          'company_name',
        ].includes(attribute.attributeKey?.toLowerCase())
    )
    .map(attribute => ({
      value: `custom_attributes.${attribute.attributeKey}`,
      label: attribute.attributeDisplayName,
    })),
]);

const makeId = () =>
  window.crypto?.randomUUID?.() ||
  `event_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const normalizeEvent = event => ({
  id: event?.id || makeId(),
  enabled: event?.enabled === true,
  name: event?.name || '',
  intent: event?.intent || '',
  examples: Array.isArray(event?.examples)
    ? event.examples.join('\n')
    : event?.examples || '',
  instructions: event?.instructions || '',
  channel: ['all', 'text', 'voice'].includes(event?.channel)
    ? event.channel
    : 'all',
  contact_label_id: event?.contact_label_id || '',
  once_per_contact: event?.once_per_contact === true,
  auto_close_after_minutes: event?.auto_close_after_minutes || '',
  auto_close_instructions:
    event?.auto_close_instructions || event?.auto_close_message || '',
  questions: Array.isArray(event?.questions)
    ? event.questions.map(question => ({
        id: question.id || makeId(),
        prompt: question.prompt || '',
        field_key: normalizeEventFieldKey(question),
      }))
    : [],
});

watch(
  () => currentAccount.value?.custom_attributes?.[EVENTS_KEY],
  value => {
    events.value = Array.isArray(value) ? value.map(normalizeEvent) : [];
    collapsedEventIds.value = new Set(events.value.map(event => event.id));
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
});

const addEvent = () => {
  if (events.value.length >= MAX_EVENTS) {
    useAlert(t('LUMERAI_SETTINGS.EVENTS.EVENT_LIMIT'));
    return;
  }
  const event = normalizeEvent({
    name: t('LUMERAI_SETTINGS.EVENTS.NEW_EVENT'),
  });
  events.value.push(event);
  collapsedEventIds.value.delete(event.id);
};

const isEventCollapsed = eventId => collapsedEventIds.value.has(eventId);
const toggleEvent = eventId => {
  const next = new Set(collapsedEventIds.value);
  if (next.has(eventId)) next.delete(eventId);
  else next.add(eventId);
  collapsedEventIds.value = next;
};

const removeEvent = index => {
  const [removedEvent] = events.value.splice(index, 1);
  collapsedEventIds.value.delete(removedEvent.id);
};
const addQuestion = event => {
  if (event.questions.length >= MAX_QUESTIONS) {
    useAlert(t('LUMERAI_SETTINGS.EVENTS.QUESTION_LIMIT'));
    return;
  }
  event.questions.push({ id: makeId(), prompt: '', field_key: '' });
};
const removeQuestion = (event, index) => event.questions.splice(index, 1);

const getAutoCloseErrorMessage = event => {
  const error = eventAutoCloseMinutesError(event.auto_close_after_minutes);
  return error ? t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_INVALID') : '';
};

const formatAutoCloseCount = event =>
  new Intl.NumberFormat('pt-BR').format(
    Number(event.auto_close_after_minutes) || 0
  );

const save = async () => {
  const activeEvents = events.value.filter(event => event.enabled);
  const invalidInactivity = events.value.some(event =>
    eventAutoCloseMinutesError(event.auto_close_after_minutes)
  );
  if (invalidInactivity) {
    useAlert(t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_INVALID'));
    return;
  }

  const invalid = activeEvents.find(
    event =>
      !event.name.trim() ||
      !event.intent.trim() ||
      (Number(event.auto_close_after_minutes) > 0 &&
        !event.auto_close_instructions.trim()) ||
      event.questions.some(
        question =>
          !question.prompt.trim() ||
          !contactFieldOptions.value.some(
            field => field.value === question.field_key
          )
      ) ||
      new Set(event.questions.map(question => question.field_key)).size !==
        event.questions.length
  );
  if (invalid) {
    useAlert(t('LUMERAI_SETTINGS.EVENTS.VALIDATION_ERROR'));
    return;
  }

  const normalized = events.value.map(event => ({
    ...event,
    name: event.name.trim(),
    intent: event.intent.trim(),
    examples: event.examples
      .split('\n')
      .map(value => value.trim())
      .filter(Boolean)
      .slice(0, 20),
    instructions: event.instructions.trim(),
    contact_label_id: event.contact_label_id || null,
    auto_close_after_minutes: Number(event.auto_close_after_minutes) || null,
    auto_close_instructions: event.auto_close_instructions.trim(),
    questions: event.questions.map(question => ({
      id: question.id,
      prompt: question.prompt.trim(),
      field_key: question.field_key.trim(),
    })),
  }));

  isSaving.value = true;
  try {
    await updateAccount({
      custom_attributes: {
        ...currentAccount.value.custom_attributes,
        [EVENTS_KEY]: normalized,
      },
    });
    collapsedEventIds.value = new Set(events.value.map(event => event.id));
    const successMessage = activeEvents.length
      ? t('LUMERAI_SETTINGS.EVENTS.SAVE_SUCCESS_ACTIVE')
      : t('LUMERAI_SETTINGS.EVENTS.SAVE_SUCCESS_INACTIVE');
    useAlert(successMessage);
  } catch {
    useAlert(t('LUMERAI_SETTINGS.EVENTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-5 rounded-xl border border-n-strong bg-white p-6 shadow-sm dark:bg-n-solid-2"
  >
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('LUMERAI_SETTINGS.EVENTS.TITLE') }}
        </h3>
        <p class="mt-1 max-w-3xl text-sm text-n-slate-11">
          {{ t('LUMERAI_SETTINGS.EVENTS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('LUMERAI_SETTINGS.EVENTS.ADD')"
        icon="i-lucide-plus"
        @click="addEvent"
      />
    </div>

    <div
      v-for="(event, eventIndex) in events"
      :key="event.id"
      class="flex flex-col gap-4 rounded-xl border border-n-strong bg-n-alpha-1 p-5"
    >
      <div class="flex items-center justify-between gap-3">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input
            v-model="event.enabled"
            type="checkbox"
            class="size-4 accent-n-brand"
          />
          {{ t('LUMERAI_SETTINGS.EVENTS.ENABLED') }}
        </label>
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-1 text-left hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :aria-expanded="!isEventCollapsed(event.id)"
          :aria-label="`${isEventCollapsed(event.id) ? t('LUMERAI_SETTINGS.EVENTS.EXPAND') : t('LUMERAI_SETTINGS.EVENTS.COLLAPSE')}: ${event.name || t('LUMERAI_SETTINGS.EVENTS.NEW_EVENT')}`"
          @click="toggleEvent(event.id)"
        >
          <span class="min-w-0">
            <span class="block truncate text-sm font-semibold text-n-slate-12">
              {{ event.name || t('LUMERAI_SETTINGS.EVENTS.NEW_EVENT') }}
            </span>
            <span
              v-if="isEventCollapsed(event.id) && event.intent"
              class="block truncate text-xs text-n-slate-10"
            >
              {{ event.intent }}
            </span>
          </span>
          <i
            class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-10 transition-transform"
            :class="{ 'rotate-180': !isEventCollapsed(event.id) }"
            aria-hidden="true"
          />
        </button>
        <Button
          :label="t('LUMERAI_SETTINGS.EVENTS.REMOVE')"
          icon="i-lucide-trash-2"
          variant="faded"
          color="ruby"
          @click="removeEvent(eventIndex)"
        />
      </div>

      <div v-if="!isEventCollapsed(event.id)" class="flex flex-col gap-4">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            v-model="event.name"
            :label="t('LUMERAI_SETTINGS.EVENTS.NAME')"
            :placeholder="t('LUMERAI_SETTINGS.EVENTS.NAME_PLACEHOLDER')"
          />
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('LUMERAI_SETTINGS.EVENTS.CHANNEL') }}
            </label>
            <div class="relative h-10 w-full">
              <select
                v-model="event.channel"
                class="select-no-native-arrow block h-full w-full appearance-none rounded-lg bg-n-alpha-black2 px-3 py-2.5 pr-10 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
              >
                <option value="all">
                  {{ t('LUMERAI_SETTINGS.EVENTS.CHANNEL_ALL') }}
                </option>
                <option value="text">
                  {{ t('LUMERAI_SETTINGS.EVENTS.CHANNEL_TEXT') }}
                </option>
                <option value="voice">
                  {{ t('LUMERAI_SETTINGS.EVENTS.CHANNEL_VOICE') }}
                </option>
              </select>
              <i
                class="i-lucide-chevron-down pointer-events-none absolute right-3 top-0 bottom-0 my-auto h-4 w-4 text-base text-n-slate-10"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <TextArea
          v-model="event.intent"
          :label="t('LUMERAI_SETTINGS.EVENTS.INTENT')"
          :placeholder="t('LUMERAI_SETTINGS.EVENTS.INTENT_PLACEHOLDER')"
          :rows="2"
        />
        <TextArea
          v-model="event.examples"
          :label="t('LUMERAI_SETTINGS.EVENTS.EXAMPLES')"
          :placeholder="t('LUMERAI_SETTINGS.EVENTS.EXAMPLES_PLACEHOLDER')"
          :rows="3"
        />
        <TextArea
          v-model="event.instructions"
          :label="t('LUMERAI_SETTINGS.EVENTS.INSTRUCTIONS')"
          :placeholder="t('LUMERAI_SETTINGS.EVENTS.INSTRUCTIONS_PLACEHOLDER')"
          :rows="4"
        />

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('LUMERAI_SETTINGS.EVENTS.CONTACT_LABEL') }}
            </label>
            <div class="relative h-10 w-full">
              <select
                v-model="event.contact_label_id"
                class="select-no-native-arrow block h-full w-full appearance-none rounded-lg bg-n-alpha-black2 px-3 py-2.5 pr-10 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
              >
                <option value="">
                  {{ t('LUMERAI_SETTINGS.EVENTS.NO_LABEL') }}
                </option>
                <option
                  v-for="label in labels"
                  :key="label.id"
                  :value="String(label.id)"
                >
                  {{ label.title }}
                </option>
              </select>
              <i
                class="i-lucide-chevron-down pointer-events-none absolute right-3 top-0 bottom-0 my-auto h-4 w-4 text-base text-n-slate-10"
                aria-hidden="true"
              />
            </div>
          </div>
          <label
            class="flex items-center gap-2 self-end pb-2 text-sm text-n-slate-12"
          >
            <input
              v-model="event.once_per_contact"
              type="checkbox"
              class="size-4 accent-n-brand"
            />
            {{ t('LUMERAI_SETTINGS.EVENTS.ONCE_PER_CONTACT') }}
          </label>
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <Input
              v-model="event.auto_close_after_minutes"
              type="number"
              min="1"
              max="10080"
              :label="t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_AFTER')"
              :placeholder="t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_DISABLED')"
              :message="getAutoCloseErrorMessage(event)"
              :message-type="getAutoCloseErrorMessage(event) ? 'error' : 'info'"
            />
            <p class="text-xs text-n-slate-10" aria-live="polite">
              {{
                t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_COUNTER', {
                  current: formatAutoCloseCount(event),
                  max: '10.080',
                })
              }}
            </p>
          </div>
          <p class="self-end pb-2 text-sm text-n-slate-11">
            {{ t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_HELP') }}
          </p>
        </div>
        <TextArea
          v-model="event.auto_close_instructions"
          :label="t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_INSTRUCTIONS')"
          :placeholder="
            t('LUMERAI_SETTINGS.EVENTS.AUTO_CLOSE_INSTRUCTIONS_PLACEHOLDER')
          "
          :rows="2"
          :disabled="!Number(event.auto_close_after_minutes)"
        />

        <div class="flex flex-col gap-3 rounded-lg border border-n-strong p-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 class="font-medium text-n-slate-12">
                {{ t('LUMERAI_SETTINGS.EVENTS.QUESTIONS_TITLE') }}
              </h4>
              <p class="text-sm text-n-slate-11">
                {{ t('LUMERAI_SETTINGS.EVENTS.QUESTIONS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              :label="t('LUMERAI_SETTINGS.EVENTS.ADD_QUESTION')"
              icon="i-lucide-plus"
              variant="faded"
              @click="addQuestion(event)"
            />
          </div>
          <div
            v-for="(question, questionIndex) in event.questions"
            :key="question.id"
            class="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_1fr_auto]"
          >
            <Input
              v-model="question.prompt"
              :label="t('LUMERAI_SETTINGS.EVENTS.QUESTION')"
              :placeholder="t('LUMERAI_SETTINGS.EVENTS.QUESTION_PLACEHOLDER')"
            />
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('LUMERAI_SETTINGS.EVENTS.FIELD_KEY') }}
              </label>
              <div class="relative h-10 w-full">
                <select
                  v-model="question.field_key"
                  class="select-no-native-arrow block h-full w-full appearance-none rounded-lg bg-n-alpha-black2 px-3 py-2.5 pr-10 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                >
                  <option disabled value="">
                    {{ t('CONTACT_SYNC.SELECT_FIELD') }}
                  </option>
                  <option
                    v-for="field in contactFieldOptions"
                    :key="field.value"
                    :value="field.value"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <i
                  class="i-lucide-chevron-down pointer-events-none absolute right-3 top-0 bottom-0 my-auto h-4 w-4 text-base text-n-slate-10"
                  aria-hidden="true"
                />
              </div>
            </div>
            <Button
              :label="t('LUMERAI_SETTINGS.EVENTS.REMOVE_QUESTION')"
              icon="i-lucide-x"
              variant="faded"
              color="ruby"
              @click="removeQuestion(event, questionIndex)"
            />
          </div>
          <p
            v-if="event.questions.length === 0"
            class="text-sm text-n-slate-10"
          >
            {{ t('LUMERAI_SETTINGS.EVENTS.NO_QUESTIONS') }}
          </p>
        </div>
      </div>
    </div>

    <p
      v-if="events.length === 0"
      class="rounded-lg border border-dashed border-n-strong p-8 text-center text-sm text-n-slate-11"
    >
      {{ t('LUMERAI_SETTINGS.EVENTS.EMPTY') }}
    </p>

    <div class="flex justify-end">
      <Button
        :label="t('LUMERAI_SETTINGS.EVENTS.SAVE')"
        icon="i-lucide-save"
        :is-loading="isSaving"
        @click="save"
      />
    </div>
  </section>
</template>
